import React, { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import ThemeSwitcher from "@/components/ThemeSwitcher";
import DemoBadge from "@/components/DemoBadge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  LayoutDashboard, Users, GraduationCap, CalendarCheck, Wallet, BookOpenCheck,
  FileSpreadsheet, CalendarDays, Megaphone, Sparkles, UserCog, Settings, LogOut, MailPlus, Menu, X, Heart, Shield, Crown
} from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/owner", label: "Owner Console", icon: Crown, roles: ["owner"] },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["*"] },
  { to: "/admin", label: "Admin Console", icon: Shield, roles: ["admin", "principal", "teacher"] },
  { to: "/children", label: "My Children", icon: Heart, roles: ["parent"] },
  { to: "/students", label: "Students", icon: GraduationCap, roles: ["admin", "principal", "teacher", "hr", "accountant"] },
  { to: "/staff", label: "Staff", icon: UserCog, roles: ["admin", "principal", "hr"] },
  { to: "/attendance", label: "Attendance", icon: CalendarCheck, roles: ["*"] },
  { to: "/fees", label: "Fees", icon: Wallet, roles: ["admin", "principal", "parent", "accountant", "hr"] },
  { to: "/homework", label: "Homework", icon: BookOpenCheck, roles: ["*"] },
  { to: "/exams", label: "Exams & Reports", icon: FileSpreadsheet, roles: ["*"] },
  { to: "/timetable", label: "Timetable", icon: CalendarDays, roles: ["*"] },
  { to: "/announcements", label: "Announcements", icon: Megaphone, roles: ["*"] },
  { to: "/ai", label: "AI Assistant", icon: Sparkles, roles: ["*"] },
  { to: "/invites", label: "Invites", icon: MailPlus, roles: ["admin", "principal"] },
  { to: "/settings", label: "Settings", icon: Settings, roles: ["*"] },
];

function initials(name) {
  return (name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const items = NAV.filter((n) => n.roles.includes("*") || n.roles.includes(user?.role));

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className={cn(
          "fixed lg:sticky top-0 left-0 z-40 h-screen w-72 shrink-0 border-r border-border bg-card/60 backdrop-blur-xl transition-transform",
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        <div className="p-6 flex items-center justify-between">
          <BrandLogo subtitle="" />
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(false)}
            data-testid="close-sidebar"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="px-3 pt-2">
          <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground px-3 pb-2">
            {user?.role} workspace
          </div>
          <nav className="space-y-1">
            {items.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setMobileOpen(false)}
                data-testid={`nav-${n.to.replace("/", "")}`}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors",
                    isActive
                      ? "bg-primary text-primary-foreground shadow-sm shadow-primary/20"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )
                }
              >
                <n.icon className="h-4 w-4" strokeWidth={2} />
                {n.label}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border">
          {(user?.role === "admin" || user?.role === "principal" || user?.role === "owner") && (
            <div className="pb-3 flex items-center justify-end gap-2">
              <DemoBadge variant="link" />
            </div>
          )}
          <div className="flex items-center gap-3 p-2 rounded-xl">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-primary/20 text-primary text-xs">{initials(user?.name)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={doLogout}
              className="rounded-xl"
              data-testid="logout-button"
              aria-label="Log out"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 h-16 flex items-center gap-3 px-4 md:px-8 border-b border-border bg-background/70 backdrop-blur-xl">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
            data-testid="open-sidebar"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">
              // {user?.role} workspace
            </div>
            <div className="text-sm font-medium">{items.find((i) => i.to === location.pathname)?.label || "Overview"}</div>
          </div>
          <DemoBadge variant="pill" />
          <ThemeSwitcher />
        </header>
        <main className="p-4 md:p-8 max-w-[1400px] mx-auto animate-fade-in-up">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
